import React, { useState } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  DatePicker,
  Select,
  Typography,
  Modal,
  List,
  Image,
} from 'antd';
import {
  PlusOutlined,
  FileTextOutlined,
  ReloadOutlined,
  PaperClipOutlined,
  EyeOutlined,
  FilePdfOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useCompany } from '../../../contexts/CompanyContext';
import { useSecureRequest } from '../../../hooks/useSecureRequest';
import { useTreasuryMovements } from '../../../hooks/useAccounting';
import {
  JournalEntry,
  TreasuryMovementItem,
  TreasuryMovementType,
  TreasuryMovementAttachment,
} from '../../../services/accountingService';
import { formatCurrency } from '../journal-entries/journalEntriesHelpers';
import CreateTreasuryMovementModal from './CreateTreasuryMovementModal';
import { JournalEntryDetailDrawer } from '../journal-entries/JournalEntryDetailDrawer';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

const MOVEMENT_TYPE_BADGES: Record<
  TreasuryMovementType,
  { label: string; color: string }
> = {
  INTERNAL_TRANSFER: { label: 'Transferencia Interna', color: 'blue' },
  PARTNER_REIMBURSEMENT: { label: 'Reembolso a Socio', color: 'green' },
  PARTNER_ADVANCE: { label: 'Anticipo a Socio', color: 'orange' },
  PARTNER_RETURN: { label: 'Devolución de Socio', color: 'cyan' },
  PARTNER_LOAN: { label: 'Préstamo de Socio', color: 'purple' },
  PARTNER_NETTING: { label: 'Compensación de Saldos', color: 'magenta' },
};

export const TreasuryMovementsTab: React.FC = () => {
  const { company } = useCompany();
  const { securePost, loading: loadingEntry } = useSecureRequest();

  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [selectedType, setSelectedType] = useState<string | undefined>(undefined);
  const [modalOpen, setModalOpen] = useState(false);

  const [selectedEntry, setSelectedEntry] = useState<JournalEntry | null>(null);
  const [entryDrawerVisible, setEntryDrawerVisible] = useState(false);

  const [attachmentsModalVisible, setAttachmentsModalVisible] = useState(false);
  const [selectedAttachments, setSelectedAttachments] = useState<{
    title: string;
    files: TreasuryMovementAttachment[];
  } | null>(null);

  const handleOpenAttachments = (files: TreasuryMovementAttachment[], title: string) => {
    setSelectedAttachments({ title, files });
    setAttachmentsModalVisible(true);
  };

  const handleOpenEntryDetail = async (entryId: number) => {
    try {
      setEntryDrawerVisible(true);
      const res = await securePost('/accounting/get-entry', {
        company_id: company.current?.id,
        company: company.current?.uid || company.current?.id,
        id: entryId,
      });
      if (res?.entry || res?.data) {
        const raw = res.entry || res.data?.entry || res.data;
        const rawLines = raw.items || raw.lines || raw.accounting_entry_items || [];
        const mappedLines = rawLines.map((l: any) => ({
          ...l,
          id: l.id,
          debit: Number(l.debit || 0),
          credit: Number(l.credit || 0),
          account_code: l.account_code || l.account?.code || '',
          account_name: l.account_name || l.account?.name || '',
          contact_name: l.contact_name || l.contacts?.name || '',
          description: l.description || '',
        }));
        setSelectedEntry({
          ...raw,
          items: mappedLines,
          lines: mappedLines,
        });
      }
    } catch (err) {
      console.error('Error fetching journal entry:', err);
    }
  };

  const { movements, isLoading, mutate } = useTreasuryMovements({
    company_id: company.current?.id,
    company_uid: company.current?.uid || undefined,
    start_date: dateRange?.[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
    end_date: dateRange?.[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
    movement_type: selectedType,
  });

  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'movement_date',
      key: 'movement_date',
      width: 110,
      render: (d: string) => (d ? dayjs(d).format('DD/MM/YYYY') : '-'),
    },
    {
      title: 'Tipo de Operación',
      dataIndex: 'movement_type',
      key: 'movement_type',
      width: 180,
      render: (type: TreasuryMovementType) => {
        const badge = MOVEMENT_TYPE_BADGES[type] || { label: type, color: 'default' };
        return <Tag color={badge.color}>{badge.label}</Tag>;
      },
    },
    {
      title: 'Origen de Fondos',
      key: 'source_account',
      render: (_: any, r: TreasuryMovementItem) => {
        if (r.movement_type === 'PARTNER_LOAN' || r.movement_type === 'PARTNER_RETURN') {
          const contact = r.contact || (r as any).partner_contact;
          if (contact) {
            return (
              <div>
                <Text strong>{contact.name}</Text>
                {contact.document_number && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      CC: {contact.document_number}
                    </Text>
                  </div>
                )}
              </div>
            );
          }
        }
        if (r.movement_type === 'PARTNER_NETTING') {
          return <Tag color="purple">Cruce Pasivo (2355)</Tag>;
        }
        const acc = r.source_account || (r as any).from_account;
        if (acc) return <Text strong>{acc.name}</Text>;
        const contact = r.contact || (r as any).partner_contact;
        if (contact) return <Text strong>{contact.name}</Text>;
        return <Text type="secondary">-</Text>;
      },
    },
    {
      title: 'Destino / Tercero',
      key: 'destination',
      render: (_: any, r: TreasuryMovementItem) => {
        if (r.movement_type === 'PARTNER_REIMBURSEMENT' || r.movement_type === 'PARTNER_ADVANCE') {
          const contact = r.contact || (r as any).partner_contact;
          if (contact) {
            return (
              <div>
                <Tag color="cyan">Socio</Tag>
                <Text strong>{contact.name}</Text>
                {contact.document_number && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      CC: {contact.document_number}
                    </Text>
                  </div>
                )}
              </div>
            );
          }
        }
        if (r.movement_type === 'PARTNER_NETTING') {
          const contact = r.contact || (r as any).partner_contact;
          return (
            <div>
              <Tag color="magenta">Cruce Activo (1325)</Tag>
              {contact && (
                <div>
                  <Text strong>{contact.name}</Text>
                </div>
              )}
            </div>
          );
        }
        const destAcc = r.destination_account || (r as any).to_account;
        if (destAcc) {
          return <Text strong>{destAcc.name}</Text>;
        }
        const contact = r.contact || (r as any).partner_contact;
        if (contact) {
          return (
            <div>
              <Text strong>{contact.name}</Text>
              {contact.document_number && (
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {contact.document_number}
                  </Text>
                </div>
              )}
            </div>
          );
        }
        return <Text type="secondary">-</Text>;
      },
    },
    {
      title: 'Monto',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right' as const,
      width: 150,
      render: (val: number) => (
        <Text strong style={{ color: '#1890ff', fontSize: 14 }}>
          {formatCurrency(val || 0)}
        </Text>
      ),
    },
    {
      title: 'Soporte / Ref',
      dataIndex: 'reference',
      key: 'reference',
      render: (ref: string, r: TreasuryMovementItem) => {
        const hasRef = Boolean(ref);
        const attachments = r.attachments || [];
        const hasAttachments = attachments && attachments.length > 0;

        if (!hasRef && !hasAttachments) {
          return <Text type="secondary">-</Text>;
        }

        return (
          <Space direction="vertical" size={2}>
            {hasRef && <Text>{ref}</Text>}
            {hasAttachments && (
              <Button
                type="link"
                size="small"
                icon={<PaperClipOutlined />}
                style={{ padding: 0, height: 'auto', fontSize: 12, display: 'inline-flex', alignItems: 'center' }}
                onClick={() => handleOpenAttachments(attachments, ref || `Movimiento #${r.id}`)}
              >
                {attachments.length === 1 ? '1 Comprobante' : `${attachments.length} Comprobantes`}
              </Button>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Comprobante Contable',
      key: 'entry',
      width: 170,
      render: (_: any, r: TreasuryMovementItem) => {
        const entry = r.accounting_entry || (r as any).entry;
        const entryId = entry?.id || r.accounting_entry_id;
        const entryNumber = entry?.entry_number || (entryId ? `CC-${entryId}` : null);
        if (!entryNumber) return <Text type="secondary">-</Text>;
        return (
          <Space size={4}>
            <FileTextOutlined style={{ color: '#52c41a' }} />
            <Tag
              color="geekblue"
              style={{ fontWeight: 600, cursor: entryId ? 'pointer' : 'default' }}
              onClick={() => {
                if (entryId) void handleOpenEntryDetail(entryId);
              }}
            >
              {entryNumber}
            </Tag>
          </Space>
        );
      },
    },
    {
      title: 'Notas / Observaciones',
      dataIndex: 'notes',
      key: 'notes',
      render: (notes: string) => notes || <Text type="secondary">-</Text>,
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <Space wrap>
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as any)}
            format="DD/MM/YYYY"
            placeholder={['Fecha Inicial', 'Fecha Final']}
          />

          <Select
            allowClear
            placeholder="Filtrar por tipo..."
            style={{ width: 220 }}
            value={selectedType}
            onChange={(val) => setSelectedType(val)}
          >
            {Object.entries(MOVEMENT_TYPE_BADGES).map(([key, badge]) => (
              <Option key={key} value={key}>
                {badge.label}
              </Option>
            ))}
          </Select>

          <Button icon={<ReloadOutlined />} onClick={() => mutate()} loading={isLoading}>
            Actualizar
          </Button>
        </Space>

        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setModalOpen(true)}
        >
          Nueva Transferencia / Operación
        </Button>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={movements}
        loading={isLoading}
        pagination={{ pageSize: 15 }}
        size="middle"
        locale={{
          emptyText:
            'No hay movimientos de tesorería registrados en el rango de fechas seleccionado.',
        }}
      />

      <CreateTreasuryMovementModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={() => {
          void mutate();
        }}
      />

      <JournalEntryDetailDrawer
        entry={selectedEntry}
        visible={entryDrawerVisible}
        loading={loadingEntry}
        onClose={() => {
          setEntryDrawerVisible(false);
          setSelectedEntry(null);
        }}
      />

      {/* Modal de Previsualización de Comprobantes Adjuntos */}
      <Modal
        title={
          <Space>
            <PaperClipOutlined style={{ color: '#1890ff', fontSize: 18 }} />
            <span>Soportes y Comprobantes: {selectedAttachments?.title}</span>
          </Space>
        }
        open={attachmentsModalVisible}
        onCancel={() => {
          setAttachmentsModalVisible(false);
          setSelectedAttachments(null);
        }}
        footer={[
          <Button key="close" type="primary" onClick={() => setAttachmentsModalVisible(false)}>
            Cerrar
          </Button>,
        ]}
        width={580}
      >
        <List
          itemLayout="horizontal"
          dataSource={selectedAttachments?.files || []}
          locale={{ emptyText: 'No hay archivos adjuntos' }}
          renderItem={(item, idx) => {
            const isImage =
              item.type?.startsWith('image/') ||
              /\.(jpg|jpeg|png|webp|gif)$/i.test(item.name || item.url);
            const isPdf = item.type === 'application/pdf' || /\.pdf$/i.test(item.name || item.url);

            return (
              <List.Item
                actions={[
                  isImage ? (
                    <Image
                      key="preview"
                      src={item.url}
                      alt={item.name}
                      width={44}
                      height={44}
                      style={{ objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                    />
                  ) : null,
                  <Button
                    key="open"
                    type="link"
                    size="small"
                    icon={isPdf ? <FilePdfOutlined /> : <EyeOutlined />}
                    onClick={() => window.open(item.url, '_blank')}
                  >
                    Ver archivo
                  </Button>,
                ].filter(Boolean)}
              >
                <List.Item.Meta
                  avatar={
                    isImage ? (
                      <PictureOutlined style={{ fontSize: 24, color: '#52c41a', marginTop: 4 }} />
                    ) : isPdf ? (
                      <FilePdfOutlined style={{ fontSize: 24, color: '#f5222d', marginTop: 4 }} />
                    ) : (
                      <PaperClipOutlined style={{ fontSize: 24, color: '#1890ff', marginTop: 4 }} />
                    )
                  }
                  title={
                    <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 500 }}>
                      {item.name || `Comprobante ${idx + 1}`}
                    </a>
                  }
                  description={
                    item.size
                      ? `${(item.size / 1024).toFixed(1)} KB`
                      : 'Comprobante bancario adjunto'
                  }
                />
              </List.Item>
            );
          }}
        />
      </Modal>
    </div>
  );
};

export default TreasuryMovementsTab;